/**
 * File: ledger.ts
 * Path: packages/config/src/accounting/ledger.ts
 * Description: The double-entry rules: what makes a journal postable, the
 * financial year a date falls in, the trial balance at any date and one
 * account's ledger with its running balance.
 *
 * The server checks every journal with validateJournal before it posts it,
 * and the admin runs the same check while a journal is typed, so the two
 * can never disagree.
 *
 * Income and expense accounts start each financial year at nil. Until the
 * year-end close exists (plan phase 7), the result of every earlier year is
 * carried in the account holding the retainedEarnings role, the way Zoho
 * Books shows it.
 */

import type {
  AccountType,
  BooksProfile,
  Journal,
  JournalDraft,
  JournalSource,
  LedgerAccount,
} from "./accountingTypes";
import { formatCents, isCents } from "./money";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** "2026-03-01" style, and a real day. */
export const isBooksDate = (value: unknown): value is string => {
  if (typeof value !== "string" || !ISO_DATE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * The first day of the financial year that `date` falls in. A year ending in
 * February runs 1 March to the end of February.
 */
export const financialYearStart = (date: string, yearEndMonth: number): string => {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  const startMonth = (yearEndMonth % 12) + 1;
  const startYear = month >= startMonth ? year : year - 1;
  return `${startYear}-${pad(startMonth)}-01`;
};

/** The last day of the financial year that `date` falls in. */
export const financialYearEnd = (date: string, yearEndMonth: number): string => {
  const start = financialYearStart(date, yearEndMonth);
  const startYear = Number(start.slice(0, 4));
  const endYear = yearEndMonth === 12 ? startYear : startYear + 1;
  const lastDay = new Date(Date.UTC(endYear, yearEndMonth, 0)).getUTCDate();
  return `${endYear}-${pad(yearEndMonth)}-${pad(lastDay)}`;
};

export const JOURNAL_LIMITS = {
  narration: 200,
  memo: 200,
  /** A manual journal; the opening balances can be longer. */
  lines: 100,
  openingLines: 400,
  /** R100 million, far past anything real, so a typo can't be posted. */
  lineCents: 10_000_000_000,
} as const;

/** True for accounts whose balance is normally a debit. */
export const isDebitNatured = (type: AccountType): boolean =>
  type === "asset" || type === "expense";

export const isIncomeStatement = (type: AccountType): boolean =>
  type === "income" || type === "expense";

/**
 * Why a journal can't be posted, in words for the screen. Empty when it can.
 * `accounts` is the profile's whole chart.
 */
export const journalIssues = (
  draft: JournalDraft,
  profile: Pick<BooksProfile, "booksStart" | "lockedThrough">,
  accounts: readonly LedgerAccount[],
  source: JournalSource = "manual",
): string[] => {
  const issues: string[] = [];
  if (!isBooksDate(draft.date)) {
    issues.push("Choose a date.");
  } else {
    if (draft.date < profile.booksStart) {
      issues.push(`The books start on ${profile.booksStart}; nothing can be dated earlier.`);
    }
    if (profile.lockedThrough && draft.date <= profile.lockedThrough) {
      issues.push(`Everything up to ${profile.lockedThrough} is locked. Unlock it first, or date the journal later.`);
    }
    if (source === "opening" && draft.date !== profile.booksStart) {
      issues.push("Opening balances are dated the day the books start.");
    }
  }
  const narration = draft.narration.trim();
  if (narration === "") issues.push("Add a narration.");
  if (narration.length > JOURNAL_LIMITS.narration) {
    issues.push(`Keep the narration under ${JOURNAL_LIMITS.narration} characters.`);
  }
  const maxLines = source === "opening" ? JOURNAL_LIMITS.openingLines : JOURNAL_LIMITS.lines;
  if (draft.lines.length < 2) issues.push("A journal needs at least two lines.");
  if (draft.lines.length > maxLines) issues.push(`A journal can have at most ${maxLines} lines.`);

  const byId = new Map(accounts.map((a) => [a.id, a]));
  let debits = 0;
  let credits = 0;
  draft.lines.forEach((line, index) => {
    const row = `Line ${index + 1}`;
    const account = byId.get(line.accountId);
    if (!account) {
      issues.push(`${row}: choose an account.`);
    } else if (account.archived) {
      issues.push(`${row}: ${account.code} ${account.name} is archived.`);
    }
    const okAmounts =
      isCents(line.debitCents) &&
      isCents(line.creditCents) &&
      line.debitCents <= JOURNAL_LIMITS.lineCents &&
      line.creditCents <= JOURNAL_LIMITS.lineCents;
    if (!okAmounts) {
      issues.push(`${row}: the amount isn't a valid rand amount.`);
      return;
    }
    if ((line.debitCents > 0) === (line.creditCents > 0)) {
      issues.push(`${row}: enter a debit or a credit, not both or neither.`);
    }
    if (line.memo.length > JOURNAL_LIMITS.memo) {
      issues.push(`${row}: keep the memo under ${JOURNAL_LIMITS.memo} characters.`);
    }
    debits += line.debitCents;
    credits += line.creditCents;
  });
  if (debits !== credits) {
    issues.push(`Debits and credits differ by ${formatCents(Math.abs(debits - credits))}.`);
  }
  return issues;
};

export const journalTotal = (draft: Pick<JournalDraft, "lines">): number =>
  draft.lines.reduce((sum, l) => sum + l.debitCents, 0);

/** The journal that cancels `journal`: every line's sides swapped. */
export const reversalLines = (journal: Pick<Journal, "lines">) =>
  journal.lines.map((l) => ({
    accountId: l.accountId,
    debitCents: l.creditCents,
    creditCents: l.debitCents,
    memo: l.memo,
    ...(l.invoice ? { invoice: l.invoice } : {}),
  }));

export interface TrialBalanceRow {
  account: LedgerAccount;
  /** Debit minus credit. */
  balanceCents: number;
  debitCents: number;
  creditCents: number;
}

export interface TrialBalance {
  asAt: string;
  yearStart: string;
  rows: TrialBalanceRow[];
  totalDebitCents: number;
  totalCreditCents: number;
  /** Income less expenses for the year to date. */
  profitCents: number;
}

const byCode = (a: LedgerAccount, b: LedgerAccount) =>
  a.code.localeCompare(b.code, "en", { numeric: true });

/**
 * Every account's balance at the end of `asAt`. Income and expense accounts
 * hold the year to date only; earlier years' results sit in retained
 * earnings. Accounts with a nil balance are left out unless `includeNil`.
 */
export const trialBalance = (
  journals: readonly Journal[],
  accounts: readonly LedgerAccount[],
  asAt: string,
  yearEndMonth: number,
  includeNil = false,
): TrialBalance => {
  const yearStart = financialYearStart(asAt, yearEndMonth);
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const balances = new Map<string, number>();
  let earlierResults = 0;
  for (const journal of journals) {
    if (journal.date > asAt) continue;
    for (const line of journal.lines) {
      const account = byId.get(line.accountId);
      if (!account) continue;
      const amount = line.debitCents - line.creditCents;
      if (isIncomeStatement(account.type) && journal.date < yearStart) {
        earlierResults += amount;
        continue;
      }
      balances.set(account.id, (balances.get(account.id) ?? 0) + amount);
    }
  }
  if (earlierResults !== 0) {
    const retained = accounts.find((a) => a.role === "retainedEarnings");
    if (retained) {
      balances.set(retained.id, (balances.get(retained.id) ?? 0) + earlierResults);
    }
  }
  const rows: TrialBalanceRow[] = [...accounts]
    .sort(byCode)
    .map((account) => {
      const balanceCents = balances.get(account.id) ?? 0;
      return {
        account,
        balanceCents,
        debitCents: balanceCents > 0 ? balanceCents : 0,
        creditCents: balanceCents < 0 ? -balanceCents : 0,
      };
    })
    .filter((row) => includeNil || row.balanceCents !== 0);
  const profitCents = -rows
    .filter((r) => isIncomeStatement(r.account.type))
    .reduce((sum, r) => sum + r.balanceCents, 0);
  return {
    asAt,
    yearStart,
    rows,
    totalDebitCents: rows.reduce((s, r) => s + r.debitCents, 0),
    totalCreditCents: rows.reduce((s, r) => s + r.creditCents, 0),
    profitCents,
  };
};

export interface LedgerRow {
  journalId: string;
  number: number;
  date: string;
  narration: string;
  memo: string;
  debitCents: number;
  creditCents: number;
  /** Debit minus credit after this line. */
  runningCents: number;
}

export interface AccountLedger {
  account: LedgerAccount;
  from: string;
  to: string;
  openingCents: number;
  rows: LedgerRow[];
  closingCents: number;
}

/**
 * One account's postings from `from` to `to` (inclusive), oldest first, with
 * the balance brought forward. An income or expense account brings forward
 * only what is in the same financial year.
 */
export const accountLedger = (
  journals: readonly Journal[],
  account: LedgerAccount,
  from: string,
  to: string,
  yearEndMonth: number,
): AccountLedger => {
  const broughtFrom = isIncomeStatement(account.type)
    ? financialYearStart(from, yearEndMonth)
    : "";
  let openingCents = 0;
  const postings: Omit<LedgerRow, "runningCents">[] = [];
  for (const journal of journals) {
    for (const line of journal.lines) {
      if (line.accountId !== account.id) continue;
      if (journal.date < from) {
        if (journal.date >= broughtFrom) openingCents += line.debitCents - line.creditCents;
        continue;
      }
      if (journal.date > to) continue;
      postings.push({
        journalId: journal.id,
        number: journal.number,
        date: journal.date,
        narration: journal.narration,
        memo: line.memo,
        debitCents: line.debitCents,
        creditCents: line.creditCents,
      });
    }
  }
  postings.sort((a, b) => a.date.localeCompare(b.date) || a.number - b.number);
  let running = openingCents;
  const rows = postings.map((p) => {
    running += p.debitCents - p.creditCents;
    return { ...p, runningCents: running };
  });
  return { account, from, to, openingCents, rows, closingCents: running };
};
